/**
 * Network bandwidth diagnostics for ICT professionals.
 * Measures throughput on the current host (Vite dev or static Firebase hosting).
 */

import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Code,
  Group,
  List,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { IconClipboard, IconDownload, IconRefresh } from "@tabler/icons-react";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { notifications } from "@mantine/notifications";
import {
  formatBandwidthSummary,
  toBandwidthExport,
} from "@/diagnostics/networkBandwidth/bandwidthAggregator";
import { getNetworkBandwidthMonitor } from "@/diagnostics/networkBandwidth/networkBandwidthMonitor";
import {
  useBandwidthMonitorSession,
  useBandwidthReport,
} from "@/diagnostics/networkBandwidth/useBandwidthReport";
import type {
  NetworkTransfer,
  TransferCategory,
} from "@/diagnostics/networkBandwidth/types";
import { useBandwidthDiagnosticsAccess } from "@/hooks/diagnostics/useBandwidthDiagnosticsAccess";

const formatMbps = (value: number): string => `${value.toFixed(2)} Mbps`;
const formatKbps = (value: number): string => `${value.toFixed(1)} Kbps`;
const formatBytes = (bytes: number): string => {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${bytes} B`;
};

const MetricCard = ({
  label,
  value,
  hint,
  color,
}: {
  label: string;
  value: string;
  hint?: string;
  color?: string;
}) => (
  <Card withBorder padding="md" radius="md">
    <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
      {label}
    </Text>
    <Text size="xl" fw={700} c={color} mt={4}>
      {value}
    </Text>
    {hint ? (
      <Text size="xs" c="dimmed" mt={4}>
        {hint}
      </Text>
    ) : null}
  </Card>
);

const TransferRow = ({ transfer }: { transfer: NetworkTransfer }) => (
  <Table.Tr>
    <Table.Td>
      <Badge size="xs" variant="outline">
        {transfer.category}
      </Badge>
    </Table.Td>
    <Table.Td>
      <Badge
        size="xs"
        color={transfer.direction === "download" ? "blue" : "orange"}
      >
        {transfer.direction}
      </Badge>
    </Table.Td>
    <Table.Td>
      <Text size="xs" lineClamp={1}>
        {transfer.url}
      </Text>
    </Table.Td>
    <Table.Td>{formatBytes(transfer.bytes)}</Table.Td>
    <Table.Td>{transfer.durationMs.toFixed(0)} ms</Table.Td>
    <Table.Td>{formatMbps((transfer.throughputBps * 8) / 1_000_000)}</Table.Td>
  </Table.Tr>
);

type TransferFilter = "all" | TransferCategory;

/** ICT-gated bandwidth diagnostics dashboard. */
export const NetworkBandwidthPage = () => {
  const { canAccess } = useBandwidthDiagnosticsAccess();
  const monitorReady = useBandwidthMonitorSession(canAccess);
  const report = useBandwidthReport(monitorReady);
  const navigate = useNavigate();
  const [transferFilter, setTransferFilter] = useState<TransferFilter>("all");
  const [scenarioMarks, setScenarioMarks] = useState<
    ReturnType<
      NonNullable<typeof window.__benchmarkBandwidth>["getScenarioMarks"]
    >
  >([]);

  const refreshScenarioMarks = useCallback(() => {
    setScenarioMarks(window.__benchmarkBandwidth?.getScenarioMarks() ?? []);
  }, []);

  const handleReset = useCallback(() => {
    window.__benchmarkBandwidth?.reset();
    refreshScenarioMarks();
  }, [refreshScenarioMarks]);

  const handleMark = useCallback(
    (name: string) => {
      window.__benchmarkBandwidth?.markScenario(name);
      refreshScenarioMarks();
    },
    [refreshScenarioMarks],
  );

  const handleCopySummary = useCallback(async () => {
    if (!report) return;
    await navigator.clipboard.writeText(formatBandwidthSummary(report));
    notifications.show({
      title: "Copied",
      message: "Bandwidth summary copied to clipboard.",
      color: "green",
    });
  }, [report]);

  const handleExport = useCallback(() => {
    if (!report) return;
    const marks = window.__benchmarkBandwidth?.getScenarioMarks() ?? [];
    const payload = toBandwidthExport(report, marks);
    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `bandwidth-report-${report.buildMode}-${Date.now()}.json`;
    anchor.click();
    URL.revokeObjectURL(url);
  }, [report]);

  const runScenario = useCallback(
    async (name: string, path: string, waitMs = 3000) => {
      const monitor = getNetworkBandwidthMonitor();
      handleMark(`before:${name}`);
      monitor.beginScenarioNavigation();
      try {
        await navigate(path);
        await new Promise<void>((resolve) => {
          window.setTimeout(() => {
            window.__benchmarkBandwidth?.markScenario(`after:${name}`);
            refreshScenarioMarks();
            resolve();
          }, waitMs);
        });
      } finally {
        monitor.endScenarioNavigation();
      }
    },
    [handleMark, navigate, refreshScenarioMarks],
  );

  const filteredTransfers = useMemo(() => {
    if (!report) return [];
    if (transferFilter === "all") return report.recentTransfers;
    return report.recentTransfers.filter((t) => t.category === transferFilter);
  }, [report, transferFilter]);

  if (!monitorReady || !report) {
    return (
      <Box p="xl">
        <Text>Starting bandwidth monitor…</Text>
      </Box>
    );
  }

  const isViteDev = report.hostDeliveryMode === "vite-dev";

  return (
    <Box p="xl" maw={1200}>
      <Stack gap="lg">
        <Group justify="space-between" align="flex-start">
          <div>
            <Group gap="xs" mb={4}>
              <Title order={2}>Network</Title>
              <Badge variant="light">{report.buildMode}</Badge>
              <Badge color={isViteDev ? "orange" : "teal"} variant="light">
                {isViteDev ? "Vite dev server" : "Static hosting"}
              </Badge>
            </Group>
            <Text c="dimmed" size="sm">
              Estimate minimum sustained and peak Internet bandwidth for optimal
              performance on this deployment.
            </Text>
          </div>
          <Group>
            <Button
              variant="subtle"
              leftSection={<IconClipboard size={16} />}
              onClick={() => void handleCopySummary()}
            >
              Copy summary
            </Button>
            <Button
              variant="light"
              leftSection={<IconRefresh size={16} />}
              onClick={handleReset}
            >
              Reset session
            </Button>
            <Button
              variant="filled"
              leftSection={<IconDownload size={16} />}
              onClick={handleExport}
            >
              Export JSON
            </Button>
          </Group>
        </Group>

        {isViteDev ? (
          <Alert
            color="orange"
            title="Development host — not representative of production"
          >
            Vite serves unbundled source modules and large dependency
            pre-bundles. Run this tool on <strong>staging</strong> or{" "}
            <strong>production</strong> (static Firebase hosting) for accurate
            sizing of deployed static assets.
          </Alert>
        ) : (
          <Alert color="blue" title="Static hosting measurements">
            Asset numbers reflect the deployed production bundle served from
            CDN/static hosting. API traffic (Medplum FHIR) is measured
            separately below.
          </Alert>
        )}

        <Card withBorder padding="md" radius="md">
          <Title order={4} mb="sm">
            Measurement procedure
          </Title>
          <List size="sm" spacing="xs">
            <List.Item>
              Click <strong>Reset session</strong>, then hard-refresh
              (Cmd+Shift+R).
            </List.Item>
            <List.Item>
              Run each scenario below and wait for the page to finish loading.
            </List.Item>
            <List.Item>
              Use <strong>peak download</strong> for burst provisioning and{" "}
              <strong>API traffic</strong> for steady-state FHIR load.
            </List.Item>
            <List.Item>
              Export JSON when finished — scenario marks are included.
            </List.Item>
          </List>
        </Card>

        <Card
          withBorder
          padding="md"
          radius="md"
          bg="var(--mantine-color-blue-light)"
        >
          <Title order={4} mb="xs">
            Recommended sizing
          </Title>
          <Text size="sm">
            <strong>Peak (burst):</strong> provision up to{" "}
            <Code>{formatMbps(report.peakDownloadMbps)}</Code> download and{" "}
            <Code>{formatMbps(report.peakUploadMbps)}</Code> upload for cold
            loads and parallel requests.
          </Text>
          <Text size="sm" mt="xs">
            <strong>API workload:</strong>{" "}
            <Code>{formatBytes(report.apiBytesDownloaded)}</Code> down /{" "}
            <Code>{formatBytes(report.apiBytesUploaded)}</Code> up this session.
          </Text>
          <Text size="xs" c="dimmed" mt="xs">
            Session: {(report.sessionDurationMs / 1000).toFixed(0)}s ·{" "}
            {report.transferCount} transfers · assets{" "}
            {formatBytes(report.assetBytesDownloaded)} · idle{" "}
            {formatKbps(report.idleDownloadKbps)}
          </Text>
        </Card>

        <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
          <MetricCard
            label="Peak download"
            value={formatMbps(report.peakDownloadMbps)}
            hint="Max 1s rolling window"
            color="blue"
          />
          <MetricCard
            label="Peak upload"
            value={formatMbps(report.peakUploadMbps)}
            hint="Max 1s rolling window"
            color="orange"
          />
          <MetricCard
            label="API downloaded"
            value={formatBytes(report.apiBytesDownloaded)}
            hint="Medplum / API proxy"
            color="teal"
          />
          <MetricCard
            label="Assets downloaded"
            value={formatBytes(report.assetBytesDownloaded)}
            hint="JS, CSS, fonts"
            color="grape"
          />
        </SimpleGrid>

        <Card withBorder padding="md" radius="md">
          <Title order={4} mb="sm">
            Scenarios
          </Title>
          <Group mb="md">
            <Button
              size="xs"
              variant="light"
              onClick={() => void runScenario("patients-list", "/Patient")}
            >
              Patients list
            </Button>
            <Button
              size="xs"
              variant="light"
              onClick={() => void runScenario("tasks", "/Task")}
            >
              Tasks
            </Button>
            <Button
              size="xs"
              variant="light"
              onClick={() => void runScenario("appointments", "/Appointment")}
            >
              Appointments
            </Button>
            <Button
              size="xs"
              variant="light"
              onClick={() =>
                void runScenario("triage", "/ServiceRequest/triage")
              }
            >
              Triage
            </Button>
            <Button
              size="xs"
              variant="light"
              onClick={() => void runScenario("cold-reload", "/Patient", 5000)}
            >
              Cold reload check
            </Button>
            <Button
              size="xs"
              variant="light"
              onClick={() => handleMark("checkpoint")}
            >
              Mark checkpoint
            </Button>
          </Group>

          {scenarioMarks.length > 0 ? (
            <Table striped withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Mark</Table.Th>
                  <Table.Th>Peak DL</Table.Th>
                  <Table.Th>API down</Table.Th>
                  <Table.Th>Assets</Table.Th>
                  <Table.Th>Transfers</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {scenarioMarks.map((mark) => (
                  <Table.Tr key={`${mark.name}-${mark.atMs}`}>
                    <Table.Td>{mark.name}</Table.Td>
                    <Table.Td>
                      {formatMbps(mark.report.peakDownloadMbps)}
                    </Table.Td>
                    <Table.Td>
                      {formatBytes(mark.report.apiBytesDownloaded)}
                    </Table.Td>
                    <Table.Td>
                      {formatBytes(mark.report.assetBytesDownloaded)}
                    </Table.Td>
                    <Table.Td>{mark.report.transferCount}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          ) : (
            <Text size="sm" c="dimmed">
              No scenario marks yet. Run a scenario to capture before/after
              snapshots.
            </Text>
          )}
        </Card>

        <Card withBorder padding="md" radius="md">
          <Group justify="space-between" mb="sm">
            <Title order={4}>Recent transfers</Title>
            <SegmentedControl
              size="xs"
              value={transferFilter}
              onChange={(value) => setTransferFilter(value as TransferFilter)}
              data={[
                { label: "All", value: "all" },
                { label: "API", value: "api" },
                { label: "Assets", value: "asset" },
                { label: "Other", value: "other" },
              ]}
            />
          </Group>
          <Table striped highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Type</Table.Th>
                <Table.Th>Dir</Table.Th>
                <Table.Th>URL</Table.Th>
                <Table.Th>Size</Table.Th>
                <Table.Th>Duration</Table.Th>
                <Table.Th>Rate</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filteredTransfers.map((transfer) => (
                <TransferRow key={transfer.id} transfer={transfer} />
              ))}
            </Table.Tbody>
          </Table>
        </Card>

        <Text size="xs" c="dimmed">
          Monitor runs only on this page. URLs are logged as origin + path (no
          query strings). Exports omit per-bucket timelines in production
          builds.
        </Text>
      </Stack>
    </Box>
  );
};
